import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { Input } from '@/components/ui/input';
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AnimatedSubscribeButton } from '@/components/ui/animated-subscribe-button';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { Loader2, Eye, EyeOff, Shield, Zap, LogIn, Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const loginSchema = z.object({
  email: z.string().min(1, 'Digite seu usuário').refine(
    (val) => {
      // Se já tem @, valida como email completo
      if (val.includes('@')) {
        return z.string().email().safeParse(val).success;
      }
      // Se não tem @, apenas valida que tem conteúdo
      return val.length > 0;
    },
    { message: 'Usuário inválido' }
  ),
  password: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';
  const sessionMessage = (location.state as { message?: string })?.message;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Show error animation when errors change
  useEffect(() => {
    if (errors.root) {
      setShowError(true);
      const timer = setTimeout(() => setShowError(false), 500);
      return () => clearTimeout(timer);
    }
  }, [errors.root]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      // Adiciona @grupod12.com se não tiver @
      const email = data.email.includes('@') 
        ? data.email 
        : `${data.email}@grupod12.com`;
      
      await signIn(email, data.password);
      navigate(from, { replace: true });
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'Usuário ou senha incorretos',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Galaxy Background */}
      <AnimatedBackground />

      <div className="w-full max-w-md relative z-10 px-4">
        {/* Card com glassmorphism */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          <CardHeader className="space-y-1 text-center">
            <div className="mb-4">
              <img 
                src="/logo-d12_sem_fundo.png" 
                alt="Grupo D12 Logo" 
                className="h-20 w-auto object-contain mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold">Bem-vindo de volta</CardTitle>
            <CardDescription>Entre com suas credenciais para continuar</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Usuário</Label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-medium z-10">
                    <Shield className="w-4 h-4" />
                  </div>
                  <Input
                    id="email"
                    type="text"
                    placeholder="seu.usuario"
                    {...register('email')}
                    disabled={isLoading}
                    className={cn(
                      'pl-10 pr-28',
                      errors.email && 'border-destructive focus-visible:ring-destructive'
                    )}
                    onFocus={() => clearErrors('email')}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-medium text-sm font-medium">
                    @grupod12.com
                  </span>
                </div>
                <AnimatePresence>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-sm text-destructive overflow-hidden"
                    >
                      {errors.email.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-medium z-10">
                    <Zap className="w-4 h-4" />
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...register('password')}
                    disabled={isLoading}
                    className={cn(
                      'pl-10 pr-10',
                      errors.password && 'border-destructive focus-visible:ring-destructive'
                    )}
                    onFocus={() => clearErrors('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-medium hover:text-high transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <AnimatePresence>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-sm text-destructive overflow-hidden"
                    >
                      {errors.password.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {errors.root && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      x: showError ? [0, -10, 10, -10, 10, 0] : 0
                    }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ 
                      duration: 0.3,
                      x: {
                        duration: 0.5,
                        ease: "easeInOut"
                      }
                    }}
                    className="border border-destructive/20 rounded-md p-3 bg-destructive/10"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                      <p className="text-sm text-destructive">{errors.root.message}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {sessionMessage && !errors.root && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="border border-yellow-500/20 rounded-md p-3 bg-yellow-500/10"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                      <p className="text-sm text-yellow-500">{sessionMessage}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatedSubscribeButton
                type="submit"
                className="w-full"
                disabled={isLoading}
                initialText={
                  <span className="group inline-flex items-center">
                    Entrar{" "}
                    <LogIn className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                }
                changeText={
                  isLoading ? (
                    <span className="inline-flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando...
                    </span>
                  ) : (
                    <span className="inline-flex items-center">
                      <Check className="mr-2 h-4 w-4" />
                      Bem-vindo!
                    </span>
                  )
                }
              />
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            {/* Footer - Empty for now */}
          </CardFooter>
        </div>

        {/* Additional info */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} Grupo D12. Todos os direitos reservados.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-white/30">
            <span>Powered by</span>
            <span className="font-semibold text-violet-400/70">D12 Tech</span>
          </div>
        </div>
      </div>
    </div>
  );
}